import { z } from 'zod';

/** Document schema version stored in `meta.schemaVersion`. Bump + add a migration in `doc/migrate.ts`. */
export const SCHEMA_VERSION = 5;

export const ITEM_KINDS = [
  'musician',
  'instrument',
  'microphone',
  'di',
  'stand',
  'riser',
  'wedge',
  'sidefill',
  'iem',
  'monitor_mixer',
  'speaker',
  'sub',
  'amp',
  'stagebox',
  'subcore',
  'console',
  'power_distro',
  'network_switch',
  'light',
  'truss',
  'generic',
] as const;
export const ItemKind = z.enum(ITEM_KINDS);
export type ItemKind = z.infer<typeof ItemKind>;

export const SIGNAL_TYPES = [
  'mic',
  'line',
  'instrument',
  'aes',
  'dante',
  'analog_multi',
  'speaker',
  'dmx',
  'ethernet',
  'power',
  'wireless',
] as const;
export const SignalType = z.enum(SIGNAL_TYPES);
export type SignalType = z.infer<typeof SignalType>;

export const CONNECTOR_TYPES = [
  'xlr3',
  'xlr5',
  'trs',
  'ts',
  'rca',
  'speakon',
  'ethercon',
  'rj45',
  'powercon',
  'powercon_true1',
  'schuko',
  'cee16',
  'cee32',
  'nema5_15',
  'iec',
  'multipin',
  'wireless',
  'other',
] as const;
export const ConnectorType = z.enum(CONNECTOR_TYPES);
export type ConnectorType = z.infer<typeof ConnectorType>;

export const POLAR_PATTERNS = [
  'cardioid',
  'supercardioid',
  'hypercardioid',
  'omni',
  'figure8',
] as const;
export const PolarPattern = z.enum(POLAR_PATTERNS);
export type PolarPattern = z.infer<typeof PolarPattern>;

export const BUILTIN_LAYER_KEYS = [
  'stage',
  'risers',
  'musicians',
  'backline',
  'microphones',
  'stands',
  'monitors',
  'pa',
  'stageboxes',
  'subcores',
  'cabling',
  'power',
  'network',
  'lighting',
  'notes',
  'ink',
] as const;
export const BuiltinLayerKey = z.enum(BUILTIN_LAYER_KEYS);
export type BuiltinLayerKey = z.infer<typeof BuiltinLayerKey>;

const id = z.string().min(1);
const finite = z.number().finite();

export const Port = z.object({
  id,
  name: z.string(),
  direction: z.enum(['in', 'out', 'bi']),
  signal: SignalType,
  connector: ConnectorType.optional(),
  /** Device requires (input of mic) or provides (console/stagebox input) +48V. */
  phantom: z.boolean().optional(),
});
export type Port = z.infer<typeof Port>;

export const PropValue = z.union([z.string(), z.number(), z.boolean()]);
export type PropValue = z.infer<typeof PropValue>;

export const ItemPower = z.object({
  sourceId: z.string().optional(),
  /** Voltages the device accepts. Empty/undefined = unknown. */
  voltages: z.array(finite).optional(),
  frequency: finite.optional(),
  watts: finite.nonnegative().optional(),
  plug: ConnectorType.optional(),
});
export type ItemPower = z.infer<typeof ItemPower>;

export const STAND_TYPES = [
  'straight',
  'boom',
  'telescopic_boom',
  'short_boom',
  'desk',
  'clamp',
  'overhead',
  'speaker_stand',
  'lighting_stand',
  'truss_lift',
] as const;
export const StandType = z.enum(STAND_TYPES);
export type StandType = z.infer<typeof StandType>;

export const STAND_BASES = ['tripod', 'low_profile_tripod', 'round', 'weighted', 'clamp'] as const;
export const StandBase = z.enum(STAND_BASES);
export type StandBase = z.infer<typeof StandBase>;

/** Holder for several mics on one stand (U70); `auto` = 2 → stereo bar, 3+ → mic bar. */
export const STAND_HOLDERS = ['auto', 'stereo_bar', 'mic_bar', 'double_clamp', 'none'] as const;
export const StandHolder = z.enum(STAND_HOLDERS);
export type StandHolder = z.infer<typeof StandHolder>;

/** Formal stand description (decision U1). Heights/lengths in metres. */
export const StandSpec = z.object({
  type: StandType,
  base: StandBase.optional(),
  minHeight: finite.positive().optional(),
  maxHeight: finite.positive().optional(),
  boomLength: finite.positive().optional(),
  /** Stereo bar on top: two mics of a stereo pair on one stand (U30); read as holder `stereo_bar`. */
  stereoBar: z.boolean().optional(),
  /** Holder for several mics (U70); unset = `stereo_bar` with `stereoBar`, else `auto`. */
  holder: StandHolder.optional(),
});
export type StandSpec = z.infer<typeof StandSpec>;

/**
 * Frequency bands for wireless mics / IEMs (OIL-52), coarse enough for pre-planning: ranges in
 * `rf/bands.ts`. `other` = known to be wireless, band outside this list.
 */
export const RF_BANDS = [
  'vhf',
  'uhf_470_608',
  'uhf_608_694',
  'uhf_694_703',
  'uhf_823_832',
  'uhf_863_865',
  'ghz_1_8',
  'ghz_2_4',
  'other',
] as const;
export const RfBand = z.enum(RF_BANDS);
export type RfBand = z.infer<typeof RfBand>;

/** RF pre-planning in steps (OIL-52): wired/wireless → band → exact frequency (MHz). */
export const RfSpec = z.object({
  link: z.enum(['wired', 'wireless']),
  band: RfBand.optional(),
  frequency: finite.positive().optional(),
});
export type RfSpec = z.infer<typeof RfSpec>;

/** Lighting fixture types (decision U12, OIL-14). */
export const LIGHT_TYPES = [
  'moving_head_spot',
  'moving_head_wash',
  'moving_head_beam',
  'par',
  'led_bar',
  'led_wash',
  'profile',
  'fresnel',
  'blinder',
  'strobe',
  'follow_spot',
] as const;
export const LightType = z.enum(LIGHT_TYPES);
export type LightType = z.infer<typeof LightType>;

export const LIGHT_MOUNTS = ['floor', 'truss', 'stand', 'pipe'] as const;
export const LightMount = z.enum(LIGHT_MOUNTS);
export type LightMount = z.infer<typeof LightMount>;

/**
 * DMX patch of a fixture: mode (e.g. "16ch" – sets the footprint), universe and start address
 * once patched (both optional: "DMX, not patched yet").
 */
export const DmxPatch = z.object({
  universe: z.number().int().positive().optional(),
  address: z.number().int().min(1).max(512).optional(),
  mode: z.string().optional(),
  /** Channels used from `address`; else read from `mode` ("16ch", "Mode 2 (24 ch)"), else 1. */
  footprint: z.number().int().min(1).max(512).optional(),
});
export type DmxPatch = z.infer<typeof DmxPatch>;

/** Formal light description (decision U12); watts live in `Item.power.watts`. */
export const LightSpec = z.object({
  type: LightType.optional(),
  mount: LightMount.optional(),
  /** Height of the fixture above the stage floor (m). */
  height: finite.nonnegative().optional(),
  dmx: DmxPatch.optional(),
});
export type LightSpec = z.infer<typeof LightSpec>;

export const TRUSS_SHAPES = ['straight', 'corner', 'circle'] as const;
export const TrussShape = z.enum(TRUSS_SHAPES);
export type TrussShape = z.infer<typeof TrussShape>;

/**
 * Formal truss description (decision U12, OIL-15). The footprint is the item's width × depth
 * (straight: width = length; circle: width = diameter); lights hang on it via `mountedOn`.
 */
export const TrussSpec = z.object({
  shape: TrussShape.optional(),
  profile: z.enum(['3pt', '4pt']).optional(),
  /** Cross-section edge (m), e.g. 0.29 / 0.3 / 0.4. */
  size: finite.positive().optional(),
  /** Height of the truss above the stage floor (m). */
  height: finite.nonnegative().optional(),
});
export type TrussSpec = z.infer<typeof TrussSpec>;

export const ChildDisplay = z.enum(['individual', 'list']);

/** Name of a symbol style (decision U12), e.g. `standard` / `capsule` for microphones. */
export const SymbolStyleName = z.string().regex(/^[a-z][a-z0-9_]{0,31}$/);
export type ChildDisplay = z.infer<typeof ChildDisplay>;

/** Built-in item fields that can be shown under the labels on the board (U42). */
/** Who supplies a material position (U67): own (inventory), the house / venue, a musician, other. */
export const SUPPLIER_KINDS = ['own', 'house', 'musician', 'other'] as const;
export const SupplierKind = z.enum(SUPPLIER_KINDS);
export type SupplierKind = z.infer<typeof SupplierKind>;
export const Supplier = z.object({
  kind: SupplierKind,
  /** Name of a musician / other supplier (kept when the musician object is deleted). */
  name: z.string().max(120).optional(),
  /** Musician object on the board (its current label wins over `name`). */
  musicianId: z.string().optional(),
});
export type Supplier = z.infer<typeof Supplier>;

export const LABEL_FIELDS = ['brandModel', 'model', 'pattern', 'stand', 'notes'] as const;
export const LabelField = z.enum(LABEL_FIELDS);
export type LabelField = z.infer<typeof LabelField>;

export const Item = z.object({
  id,
  kind: ItemKind,
  layerId: id,
  label: z.string(),
  /** Area the item lies in (frame-relative x/y); null = free (world x/y) or in the tray (`unplaced`). */
  frameId: z.string().nullable(),
  /** In the 'Not placed' tray: no position yet (S6 replaced by U99). */
  unplaced: z.literal(true).optional(),
  x: finite,
  y: finite,
  rotation: finite,
  z: finite.optional(),
  width: finite.positive().optional(),
  depth: finite.positive().optional(),
  catalogRef: z.string().optional(),
  brand: z.string().optional(),
  model: z.string().optional(),
  pattern: PolarPattern.optional(),
  tags: z.array(z.string()),
  notes: z.string().optional(),
  props: z.record(z.string(), PropValue),
  ports: z.array(Port).optional(),
  power: ItemPower.optional(),
  /** Free-text name of a group (e.g. "Drums"), used by quick entry `@drums`. */
  group: z.string().optional(),
  /** Only for kind `stand`. */
  stand: StandSpec.optional(),
  /** Mic/DI mounted on this stand item. */
  mountedOn: z.string().optional(),
  /** Parent item (e.g. the drum kit a mic belongs to, decision U2). */
  parentId: z.string().optional(),
  /** How this item's children are drawn on the canvas: individually or as one list box. */
  childDisplay: ChildDisplay.optional(),
  /** User colour for the symbol (CSS hex `#rrggbb`), decision U3. */
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  /** Symbol style of this item, overrides the board's `meta.symbolStyle` (decision U12). */
  symbol: SymbolStyleName.optional(),
  /** Wired or wireless link of IEMs / mics, with band and frequency (OIL-52). */
  rf: RfSpec.optional(),
  /** Only for kind `light` (OIL-14). */
  light: LightSpec.optional(),
  /** Only for kind `truss` (OIL-15). */
  truss: TrussSpec.optional(),
  /**
   * Drawn individually although an ancestor shows its children as a list (dragged out of the
   * list box, OIL-37); still a child of that parent.
   */
  detached: z.boolean().optional(),
  /**
   * Label placed by hand (U28): centre of the label box relative to the item centre (m). Unset =
   * automatic placement.
   */
  labelOffset: z.object({ dx: finite, dy: finite }).optional(),
  /** Stereo pair of two mics / DIs (U30): shared pair id, and which side this one is. */
  pair: z.object({ id, side: z.enum(['L', 'R']) }).optional(),
  /** Drawing order within its layer (U31): higher is in front. Unset = 0. */
  stack: z.number().int().optional(),
  /** Size factor of the list box of a parent drawn as a list (U29), 1 = default. */
  listScale: z.number().min(0.5).max(3).optional(),
  /**
   * List box placed by hand (U37): its top-left corner relative to the item centre (m). Unset =
   * automatic placement next to the item.
   */
  listOffset: z.object({ dx: finite, dy: finite }).optional(),
  /** The label is not drawn on the board (U37). */
  labelHidden: z.boolean().optional(),
  /**
   * Lines under this object's label (U64), overriding the board's `labelFields` – e.g. brand and
   * model only for the vocal mics. Unset = as the board; [] = none.
   */
  labelFields: z.array(LabelField).optional(),
  /** The object is not drawn on the canvas; its list box, channels and lists stay (U37). */
  hidden: z.boolean().optional(),
  /** Deliberately left in the unplaced tray: not counted, no "not placed" hint (U54). */
  unplacedIgnored: z.boolean().optional(),
  /** Who supplies this object (U67); unset = the material row's supplier, else own. */
  supplier: Supplier.optional(),
});
export type Item = z.infer<typeof Item>;

export const PortRef = z.object({ itemId: id, portId: id });
export type PortRef = z.infer<typeof PortRef>;

export const Connection = z.object({
  id,
  from: PortRef,
  to: PortRef,
  cableType: z.string().optional(),
  length: finite.positive().optional(),
  label: z.string().optional(),
  color: z.string().optional(),
  /** Runs through a sub-core/multicore lane. */
  via: z.object({ itemId: id, lane: z.number().int().positive() }).optional(),
});
export type Connection = z.infer<typeof Connection>;

export const Channel = z.object({
  id,
  number: z.number().int().positive(),
  name: z.string(),
  sourceItemId: z.string().optional(),
  sourcePortId: z.string().optional(),
  /** Informal source description when no item exists. */
  sourceText: z.string().optional(),
  phantom: z.boolean().optional(),
  insert: z.string().optional(),
  group: z.string().optional(),
  stand: z.string().optional(),
  /** Informal patch point, e.g. "SB1/5". */
  patch: z.string().optional(),
  /**
   * Sub-core point the source plugs into, e.g. "Sub A/3" (U44) – kept next to `patch`: the
   * sub-core is only a helper, the stage-box channel stays the reference.
   */
  subPatch: z.string().optional(),
  notes: z.string().optional(),
});
export type Channel = z.infer<typeof Channel>;

export const MixSend = z.object({ channelId: id, level: finite.optional() });
export type MixSend = z.infer<typeof MixSend>;

export const Mix = z.object({
  id,
  number: z.number().int().positive(),
  name: z.string(),
  kind: z.enum(['monitor', 'iem', 'foh', 'fx']),
  stereo: z.boolean(),
  sends: z.array(MixSend),
  /** Wedges / sidefills / IEM packs fed by this mix – the same signal on N monitors. */
  outputItemIds: z.array(id),
  /** Stage-box outputs the mix plays on, informal ("SB1/5"); mono one, stereo two (U77). */
  outputs: z.array(z.string().max(60)).max(8).optional(),
  notes: z.string().optional(),
});
export type Mix = z.infer<typeof Mix>;

export const PowerSource = z.object({
  id,
  name: z.string(),
  voltage: finite.positive(),
  frequency: finite.positive(),
  plug: ConnectorType,
  maxAmps: finite.positive().optional(),
  /** Optional placed item (power distro) representing this source on stage. */
  itemId: z.string().optional(),
  /** A transformer allows any device voltage. */
  transformer: z.boolean().optional(),
});
export type PowerSource = z.infer<typeof PowerSource>;

export const FRAME_KINDS = ['stage', 'foh', 'monitor', 'area'] as const;
export const FrameKind = z.enum(FRAME_KINDS);
export type FrameKind = z.infer<typeof FrameKind>;

export const Frame = z.object({
  id,
  kind: FrameKind,
  name: z.string(),
  x: finite,
  y: finite,
  width: finite.positive(),
  depth: finite.positive(),
  zones: z.boolean(),
});
export type Frame = z.infer<typeof Frame>;

export const LAYER_KINDS = ['objects', 'ink'] as const;
export const LayerKind = z.enum(LAYER_KINDS);
export type LayerKind = z.infer<typeof LayerKind>;

export const Layer = z.object({
  id,
  key: BuiltinLayerKey.optional(),
  name: z.string(),
  visible: z.boolean(),
  locked: z.boolean(),
  order: z.number(),
  /** What the layer holds: stage/free-form objects (default) or ink strokes (decision I2). */
  kind: LayerKind.optional(),
  /**
   * Hidden by an owner/admin for the whole board (decision U13): not listed in the layers panel
   * or presets, its objects are not drawn. Restored via "Hidden layers…".
   */
  hiddenInPanel: z.boolean().optional(),
  /** Visible but greyed out: low saturation, still readable and editable (decision U81). */
  dimmed: z.boolean().optional(),
});
export type Layer = z.infer<typeof Layer>;

export const FREEFORM_KINDS = [
  'sticky',
  /** Comment point: a conspicuous star that opens its discussion when clicked. */
  'comment',
  'text',
  'rect',
  'ellipse',
  'diamond',
  'arrow',
  'line',
  /** Dimension line (OIL-17): end ticks and the measured length (text overrides it). */
  'dimension',
  'image',
  'table',
] as const;
export const FreeformKind = z.enum(FREEFORM_KINDS);
export type FreeformKind = z.infer<typeof FreeformKind>;

export const Endpoint = z.union([z.object({ elementId: id }), z.object({ x: finite, y: finite })]);
export type Endpoint = z.infer<typeof Endpoint>;

/** Text formatting of a shape / note (OIL-45); every field optional, defaults per kind. */
export const TextStyle = z.object({
  size: z.enum(['s', 'm', 'l', 'xl']).optional(),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  underline: z.boolean().optional(),
  /** Horizontal alignment inside the box. */
  align: z.enum(['start', 'center', 'end']).optional(),
  /** Vertical alignment inside the box. */
  valign: z.enum(['top', 'middle', 'bottom']).optional(),
  /** Inside the shape, or outside next to it (the text grows away from the shape). */
  anchor: z.enum(['inside', 'left', 'right', 'top', 'bottom']).optional(),
});
export type TextStyle = z.infer<typeof TextStyle>;

export const FreeformElement = z.object({
  id,
  kind: FreeformKind,
  x: finite,
  y: finite,
  w: finite.nonnegative(),
  h: finite.nonnegative(),
  rotation: finite,
  text: z.string().optional(),
  textStyle: TextStyle.optional(),
  color: z.string().optional(),
  from: Endpoint.optional(),
  to: Endpoint.optional(),
  cells: z.array(z.array(z.string())).optional(),
  fileId: z.string().optional(),
  /** Image rendered from a PDF page (decision U8): the original PDF file … */
  sourceFileId: z.string().optional(),
  /** … and its 1-based page number, so the page can be re-rendered later. */
  sourcePage: z.number().int().positive().optional(),
  /** Locked elements cannot be selected or moved on the canvas (e.g. a stage underlay, U8). */
  locked: z.boolean().optional(),
  authorId: z.string().optional(),
  authorName: z.string().optional(),
  /** On top of the stage frame, freeform elements live on the notes layer. */
  layerId: z.string().optional(),
  /** Drawing order within its layer (U31): higher is in front. Unset = 0. */
  stack: z.number().int().optional(),
});
export type FreeformElement = z.infer<typeof FreeformElement>;

export const INK_TOOLS = ['pen', 'pencil', 'fineliner', 'highlighter'] as const;
export const InkTool = z.enum(INK_TOOLS);
export type InkTool = z.infer<typeof InkTool>;

/**
 * One committed ink stroke (decisions I1–I12, J3/J4). Immutable value: erasing deletes the stroke
 * and adds the remaining pieces. `points` is the packed point list (see `ink/codec.ts`), in board
 * coordinates (metres) or – when `attachedTo` is set – in the element's local coordinates
 * (centre origin, unrotated) measured at `attachedSize`; resizing the element scales the
 * positions, never the width.
 */
export const InkStroke = z.object({
  id,
  layerId: id,
  tool: InkTool,
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  /** Nominal width in metres. */
  width: finite.positive(),
  points: z.string(),
  /** Average pencil tilt in degrees (0 = upright … 90 = flat), widens and softens pencil strokes. */
  tilt: finite.min(0).max(90).optional(),
  /** Free-form element the stroke is attached to (moves / scales / rotates with it). */
  attachedTo: z.string().optional(),
  /** Element size when the local points were recorded. */
  attachedSize: z.object({ w: finite.positive(), h: finite.positive() }).optional(),
  authorId: z.string().optional(),
  authorName: z.string().optional(),
  /** Epoch milliseconds. */
  createdAt: z.number().int().nonnegative(),
});
export type InkStroke = z.infer<typeof InkStroke>;

export const PROPERTY_TYPES = ['text', 'number', 'boolean', 'select'] as const;
export const PropertyType = z.enum(PROPERTY_TYPES);
export type PropertyType = z.infer<typeof PropertyType>;

/** User-defined custom property (decision U3), e.g. "owner". Values live in `Item.props[key]`. */
export const PropertyDef = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/),
  label: z.string().min(1).max(60),
  type: PropertyType,
  /** Choices for `select`. */
  options: z.array(z.string().min(1)).optional(),
  /** Show the value next to the item label on the canvas. */
  showOnCanvas: z.boolean().optional(),
});
export type PropertyDef = z.infer<typeof PropertyDef>;

/** Categories of the material list (decision U11). */
export const MATERIAL_CATEGORIES = [
  'mics',
  'dis',
  'stands',
  'monitors',
  'iem',
  'pa',
  'amps',
  'stageboxes',
  'subcores',
  'consoles',
  'network',
  'power',
  'lighting',
  'cables_audio',
  'cables_power',
  'cables_network',
  'adapters',
  'backline',
  'risers',
  'furniture',
  'misc',
] as const;
export const MaterialCategory = z.enum(MATERIAL_CATEGORIES);
export type MaterialCategory = z.infer<typeof MaterialCategory>;

/** One position of an imported (external) material list. */
export const MaterialImportRow = z.object({
  category: MaterialCategory.optional(),
  name: z.string().min(1),
  qty: finite.nonnegative(),
  notes: z.string().optional(),
});
export type MaterialImportRow = z.infer<typeof MaterialImportRow>;

/** One position of the workspace's own inventory (decision U15). */
export const InventoryEntry = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  /** Catalog entry (`brand/model` ref); without it the entry is matched by its name. */
  catalogRef: z.string().max(200).optional(),
  category: MaterialCategory.optional(),
  quantity: z.number().int().min(0).max(100_000),
  /** Cable length in metres (cables are allocated per length). */
  length: finite.positive().max(10_000).optional(),
  serials: z.array(z.string().max(100)).max(10_000).optional(),
  location: z.string().max(200).optional(),
  notes: z.string().max(2000).optional(),
});
export type InventoryEntry = z.infer<typeof InventoryEntry>;

const percent = finite.min(0).max(1000);

/** Per-row overrides of the generated material list (U67, U69, U71), keyed by row key. */
export const MaterialRowSettings = z.object({
  /** Supplier of the row's sources that have none of their own (U67). */
  supplier: Supplier.optional(),
  /** Spares of this row, also 0 (U69); unset = the category percentage. */
  spares: z.number().int().min(0).max(100_000).optional(),
  /** Own name of the position (U71, keyed by the base row key); the original stays in the details. */
  name: z.string().max(200).optional(),
});
export type MaterialRowSettings = z.infer<typeof MaterialRowSettings>;

/** A material position without an object on the board (U69), e.g. "Gaffa tape". */
export const MaterialExtra = z.object({
  id,
  category: MaterialCategory,
  name: z.string().max(200),
  count: z.number().int().min(0).max(100_000),
  /** Unset = the category percentage. */
  spares: z.number().int().min(0).max(100_000).optional(),
  supplier: Supplier.optional(),
  notes: z.string().max(2000).optional(),
});
export type MaterialExtra = z.infer<typeof MaterialExtra>;

/** Several material rows shown as one position (U71): its name and the members' base row keys. */
export const MaterialMerge = z.object({
  id,
  name: z.string().max(200),
  rowKeys: z.array(z.string()).min(1).max(500),
});
export type MaterialMerge = z.infer<typeof MaterialMerge>;

/**
 * Material list settings per board (decision U11), stored in `meta.materialList`. Every field is
 * optional; missing fields fall back to the defaults (`resolveMaterialConfig`), so no migration.
 */
export const MaterialListSettings = z.object({
  /** Category switches; missing = shown. */
  categories: z.partialRecord(MaterialCategory, z.boolean()).optional(),
  /** Spare percentage for the categories that carry spares by default (mics, DIs, cables, adapters). */
  sparePercent: percent.optional(),
  /** Per-category spare percentage, overrides the default for that category. */
  spareByCategory: z.partialRecord(MaterialCategory, percent).optional(),
  /** Cable stock lengths in metres (estimated lengths round up to the next one). */
  stockLengths: z.array(finite.positive()).max(40).optional(),
  /** Routing allowance: distance × factor + perEnd metres at each end. */
  cableAllowance: z
    .object({ factor: finite.min(1).max(5), perEnd: finite.min(0).max(20) })
    .optional(),
  groupBy: z.enum(['category', 'none']).optional(),
  /** Imported name → catalog ref or modelled name (reconciliation aliases). */
  aliases: z.record(z.string(), z.string()).optional(),
  /** The last imported external material list (kept for revisiting the reconciliation). */
  imported: z
    .object({
      source: z.string().optional(),
      importedAt: z.number().int().nonnegative(),
      rows: z.array(MaterialImportRow).max(2000),
    })
    .optional(),
  /** Reconciliation row keys the user chose to ignore. */
  ignored: z.array(z.string()).optional(),
  /** Name shown for the own supplier (U67); unset = the signed-in user's name. */
  ownName: z.string().max(120).optional(),
  /** Per-row overrides (U67, U69, U71). */
  rows: z.record(z.string(), MaterialRowSettings).optional(),
  /** Extra positions without an object (U69), in their entered order. */
  extras: z.array(MaterialExtra).max(1000).optional(),
  /** Merged positions (U71). */
  merges: z.array(MaterialMerge).max(500).optional(),
});
export type MaterialListSettings = z.infer<typeof MaterialListSettings>;

/** Rider document details of a board (U46, OIL-20). */
export const RiderContact = z.object({
  id,
  role: z.string().max(80),
  name: z.string().max(120),
  phone: z.string().max(60).optional(),
  email: z.string().max(200).optional(),
});
export type RiderContact = z.infer<typeof RiderContact>;
export const RiderScheduleEntry = z.object({
  id,
  time: z.string().max(40),
  label: z.string().max(200),
  /** Day of the entry (YYYY-MM-DD); else the rider date (U85). */
  date: z.string().max(10).optional(),
  /** End time (HH:MM); else the next entry's start (U85). */
  end: z.string().max(40).optional(),
});
export type RiderScheduleEntry = z.infer<typeof RiderScheduleEntry>;
export const RiderSection = z.object({
  id,
  title: z.string().max(120),
  body: z.string().max(20_000),
});
export type RiderSection = z.infer<typeof RiderSection>;
export const RiderInfo = z.object({
  artist: z.string().max(200).optional(),
  tour: z.string().max(200).optional(),
  date: z.string().max(60).optional(),
  venue: z.string().max(200).optional(),
  version: z.string().max(60).optional(),
  contacts: z.array(RiderContact).optional(),
  schedule: z.array(RiderScheduleEntry).optional(),
  sections: z.array(RiderSection).optional(),
});
export type RiderInfo = z.infer<typeof RiderInfo>;

export const Meta = z.object({
  schemaVersion: z.number().int().positive(),
  name: z.string(),
  units: z.enum(['metric', 'imperial']),
  /** Board-level custom property definitions (decision U3). */
  propertyDefs: z.array(PropertyDef).optional(),
  /** Material list settings, aliases and the imported list (decision U11). */
  materialList: MaterialListSettings.optional(),
  /** Symbol style per item kind (decision U12); missing kinds use their default style. */
  symbolStyle: z.partialRecord(ItemKind, SymbolStyleName).optional(),
  /** Layers without content are not listed in the layers panel (decision U13, default true). */
  hideEmptyLayers: z.boolean().optional(),
  /** Polar pattern hints around microphones (U39, default true). */
  polarHints: z.boolean().optional(),
  /** Mix (bus) numbers on wedges / monitors on the canvas (U40, default true). */
  mixBadges: z.boolean().optional(),
  /** Stage-box output numbers on monitors (U77, default true). */
  outputBadges: z.boolean().optional(),
  /** Prefix of the bus number on monitors (U77, default "bus"). */
  busPrefix: z.enum(['bus', 'mix']).optional(),
  /** Item fields shown under the labels on the board, in this order (U42). */
  labelFields: z.array(LabelField).optional(),
  /** UHF TV channels (21–69) on air at the venue – no wireless carriers there (U45). */
  rfTvChannels: z.array(z.number().int().min(21).max(69)).optional(),
  /** Cover, contacts, schedule and free-text sections of the rider document (U46). */
  rider: RiderInfo.optional(),
  /** IANA time zone of the venue (U85, default Europe/Berlin). */
  timezone: z.string().max(64).optional(),
});
export type Meta = z.infer<typeof Meta>;

/** Plain JSON projection of a board document (REST, snapshots, export, tests). */
export const BoardData = z.object({
  meta: Meta,
  frames: z.record(z.string(), Frame),
  layers: z.record(z.string(), Layer),
  items: z.record(z.string(), Item),
  connections: z.record(z.string(), Connection),
  channels: z.record(z.string(), Channel),
  mixes: z.record(z.string(), Mix),
  power: z.record(z.string(), PowerSource),
  freeform: z.record(z.string(), FreeformElement),
  /** Ink strokes (schema v2); documents written before v2 have none. */
  ink: z.record(z.string(), InkStroke).default({}),
});
export type BoardData = z.infer<typeof BoardData>;

export const COLLECTIONS = [
  'frames',
  'layers',
  'items',
  'connections',
  'channels',
  'mixes',
  'power',
  'freeform',
  'ink',
] as const;
export type CollectionName = (typeof COLLECTIONS)[number];

export const COLLECTION_SCHEMAS = {
  frames: Frame,
  layers: Layer,
  items: Item,
  connections: Connection,
  channels: Channel,
  mixes: Mix,
  power: PowerSource,
  freeform: FreeformElement,
  ink: InkStroke,
} as const;

export type EntityOf<C extends CollectionName> = z.infer<(typeof COLLECTION_SCHEMAS)[C]>;

/** Equipment catalog entry (built-in or workspace catalog). */
export const CatalogEntry = z.object({
  ref: z.string().min(1),
  kind: ItemKind,
  brand: z.string(),
  model: z.string(),
  /** Search aliases for quick entry, lower-case (e.g. "sm58", "58"). */
  aliases: z.array(z.string()),
  ports: z.array(Port),
  pattern: PolarPattern.optional(),
  /** Needs +48V phantom power. */
  phantom: z.boolean().optional(),
  power: ItemPower.optional(),
  width: finite.positive().optional(),
  depth: finite.positive().optional(),
  stand: StandSpec.optional(),
  /** Light type / mount / DMX mode defaults (OIL-14). */
  light: LightSpec.optional(),
  truss: TrussSpec.optional(),
  description: z.string().optional(),
  /** Default props copied to items (e.g. `passThrough: true` for wireless receivers). */
  props: z.record(z.string(), PropValue).optional(),
});
export type CatalogEntry = z.infer<typeof CatalogEntry>;
export const Catalog = z.object({ version: z.literal(1), entries: z.array(CatalogEntry) });
export type Catalog = z.infer<typeof Catalog>;
